/**
 * fqi.ts — one round of Fitted Q-Iteration.
 *
 * WHAT FQI IS (the canonical stable RL method, Ernst & Geurts 2005):
 *
 *   Loop k = 0, 1, 2, …:
 *     1. Collect transitions (s, a, r, s') into a replay buffer using some
 *        behaviour policy (random at first, then ε-greedy on Q_{k-1}).
 *     2. Compute bootstrap targets with the FROZEN Q_{k-1}:
 *          y_i = r_i + γ · max_a' Q_{k-1}(s'_i, a')    (0 if s' is terminal)
 *     3. REFIT Q_k by regression: minimise Σ (Q_k(s_i, a_i) − y_i)².
 *
 * The target is computed with the OLD Q, then held fixed while we solve for
 * the NEW Q. This is the key difference from online TD(0), where the target
 * moves with every update and the deadly triad sends non-linear approximators
 * diverging. With a frozen target and a convex regression, the new Q is the
 * exact minimiser — there is no learning-rate instability, no divergence.
 *
 * In this implementation, each "round" = one fit on the whole accumulated
 * buffer. The collection step is interleaved with the live demo: main.ts's
 * tick loop runs ε-greedy rollouts (the visible agent motion), and at each
 * episode boundary fqi.fitRound() is called to advance Q by one step. So
 * `roundCount` is the actual learning-progress counter.
 */

import { LinearQ } from './qnet';
import { Replay } from './replay';
import { N_FEATURES, N_ACTIONS } from './features';

export interface FitRoundResult {
  /** Round index after this fit (1-based). */
  roundCount: number;
  /** ε used by the collection policy in the round just completed. */
  epsilonUsed: number;
  /** Mean TD-ish error: mean |Q_new(s,a) − y| over the buffer. A measure of
   *  how much the new Q moved relative to the bootstrap target. */
  fitLoss: number;
  /** Number of transitions in the buffer at fit-time. */
  bufferSize: number;
}

/**
 * Refit `q` on the current replay buffer.
 *
 * Workflow:
 *   1. Predict Q(s'_i, ·) for all next-states using the CURRENT q (frozen
 *      during the regression below).
 *   2. Build the target y_i = r_i + γ · max_a' Q(s'_i, a') (or r_i if done).
 *   3. Refit q on (X_taken, y) via ridge regression.
 *
 * `rngBulk` is a scratch buffer of length (size × N_ACTIONS) for the
 * per-candidate next-state Q values. Allocate once and reuse.
 */
export function fitRound(
  q: LinearQ,
  replay: Replay,
  rngBulk: Float64Array,
): FitRoundResult {
  const B = replay.size;
  if (B === 0) {
    return { roundCount: 0, epsilonUsed: 0, fitLoss: 0, bufferSize: 0 };
  }
  // Step 1: predict Q(s'_i, ·) for every transition's next-state, in one
  // batched matvec. replay.Xnext is (B × N_ACTIONS × N_FEATURES); the bulk
  // output is (B × N_ACTIONS) flat.
  q.predictBatch(replay.Xnext, B, rngBulk);

  // Step 2: build the target y_i = r_i + γ · max_a' Q(s'_i, a'), gated by done.
  const y = new Float64Array(B);
  const gamma = q.gamma;
  for (let i = 0; i < B; i++) {
    const qOff = i * N_ACTIONS;
    let qMax = -Infinity;
    for (let a = 0; a < N_ACTIONS; a++) {
      const v = rngBulk[qOff + a];
      if (v > qMax) qMax = v;
    }
    const r = replay.R[i];
    y[i] = replay.Done[i] ? r : r + gamma * qMax;
  }

  // Capture pre-fit predictions for a rough fit-loss measure.
  const before = new Float64Array(B);
  for (let i = 0; i < B; i++) {
    let s = q.w[N_FEATURES];
    const off = i * N_FEATURES;
    for (let j = 0; j < N_FEATURES; j++) s += q.w[j] * replay.X[off + j];
    before[i] = s;
  }

  // Step 3: ridge regression on (X_taken, y). Exact closed-form solve.
  q.fit(replay.X, y, B);

  // fitLoss = mean |Q_new(s,a) − Q_old(s,a)| over the buffer.
  let lossSum = 0;
  for (let i = 0; i < B; i++) {
    let s = q.w[N_FEATURES];
    const off = i * N_FEATURES;
    for (let j = 0; j < N_FEATURES; j++) s += q.w[j] * replay.X[off + j];
    lossSum += Math.abs(s - before[i]);
  }
  return {
    roundCount: 0, // caller fills in (they own the round counter)
    epsilonUsed: 0, // caller fills in
    fitLoss: lossSum / B,
    bufferSize: B,
  };
}

/**
 * ε-greedy ε-schedule across FQI rounds. Matches the validated v5 prototype:
 * round 0 = random; round k ≥ 1 = max(0.15, 0.9 − 0.07·k). The agent starts
 * exploring broadly and tightens to mostly-greedy by round ~10.
 */
export function epsilonForRound(round: number): number {
  if (round === 0) return 1.0;
  return Math.max(0.15, 0.9 - 0.07 * round);
}
